import React, { FC } from 'react';
import { Label } from 'reactstrap';
import classNames from 'classnames';
import { FlexRow, BodyText, BodyTextSmall, FlexCol, FlexProps } from '@minecraft.atoms';
import { IconWithTooltip } from '../IconWithTooltip';

const isValidationVisible = (validationProp: any) => {
  return (
    typeof validationProp === 'object' &&
    typeof validationProp?.valid === 'boolean' &&
    typeof validationProp?.message === 'string'
  );
};

export interface DisplayedFieldValidation {
  valid: boolean;
  message: string;
}

export type FieldWrapperValidation = false | DisplayedFieldValidation;

export const ValidationText: FC<DisplayedFieldValidation & { 'data-testid': string }> = ({
  valid,
  message,
  'data-testid': testId,
}) => {
  if (!message) return null;

  return (
    <FlexRow className="cn_w-full cn_mt-1" data-testid={testId}>
      <BodyTextSmall color={valid ? 'default' : 'error'}>{message}</BodyTextSmall>
    </FlexRow>
  );
};

export interface FieldWrapperProps {
  label?: string;
  helpText?: string;
  validation?: FieldWrapperValidation;
  id?: string;
  disabled?: boolean;
  tooltip?: React.ReactNode;
  helpTextAbove?: string;
  gap?: FlexProps['gap'];
  'data-qa-id'?: string;
  'data-testid'?: string;
}

export const FieldWrapper: FC<FieldWrapperProps> = ({
  id,
  label,
  helpText,
  validation = false,
  disabled = false,
  children,
  tooltip,
  helpTextAbove,
  gap,
  'data-qa-id': qaId,
  'data-testid': testId,
}) => {
  const displayValidation = isValidationVisible(validation);
  const testIdBase = testId || id || 'field-wrapper';

  return (
    <FlexCol data-testid={`${testIdBase}-group`} data-qa-id={qaId} className="cn_w-full" wrap="nowrap" gap={gap}>
      {label && (
        <FlexRow
          className="cn_w-full"
          justifyContent="space-between"
          alignItems="center"
          data-testid={`${testIdBase}-label-tooltip-wrapper`}
          wrap="nowrap"
        >
          <Label
            htmlFor={id}
            data-testid={`${testIdBase}-label`}
            className={classNames('cn_w-full', { 'cn_mb-1': !helpTextAbove, 'cn_mb-0': helpTextAbove })}
          >
            <BodyText fontWeight="semibold" color={disabled ? 'disabled' : 'default'}>
              {label}
            </BodyText>
          </Label>
          {tooltip && (
            <IconWithTooltip
              iconColor="info"
              id={`${testIdBase}-tooltip`}
              iconName="info"
              placement="top-end"
              tooltipContent={tooltip}
              aria-label={`${testIdBase}-tooltip`}
            />
          )}
        </FlexRow>
      )}
      {helpTextAbove && (
        <FlexRow className="cn_w-full" mb="0" data-testid={`${testIdBase}-helptxt-above`}>
          <BodyTextSmall color="default" className="cn_wrap-text">
            {helpTextAbove}
          </BodyTextSmall>
        </FlexRow>
      )}
      {children}
      {helpText && !displayValidation && (
        <FlexRow className="cn_w-full cn_mt-1" data-testid={`${testIdBase}-helptxt`}>
          <BodyTextSmall color="default" className="cn_wrap-text">
            {helpText}
          </BodyTextSmall>
        </FlexRow>
      )}
      {!disabled && displayValidation && (
        <ValidationText {...(validation as DisplayedFieldValidation)} data-testid={`${testIdBase}-validation`} />
      )}
    </FlexCol>
  );
};
